import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useLang, scrollToId } from "@/i18n";
import { EASE } from "./Reveal";

const LiveClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return <span data-testid="hero-feed-clock">{now.toLocaleTimeString("ro-RO", { hour12: false })}</span>;
};

const Corner = ({ pos }) => (
  <span className={`absolute h-6 w-6 border-red-500 ${pos}`} />
);

const CameraFeed = ({ rx, ry, y, label }) => (
  <motion.div style={{ rotateX: rx, rotateY: ry, y, transformPerspective: 1200 }} className="relative w-full aspect-[4/5] sm:aspect-[4/3] lg:aspect-[4/5]" data-testid="hero-camera-feed">
    <div className="absolute inset-0 clip-tac overflow-hidden bg-zinc-900">
      <motion.img
        src="/img/hero.jpg" alt="Camere CCTV montate pe fațadă"
        initial={{ scale: 1.25, filter: "grayscale(1) brightness(0.3)" }}
        animate={{ scale: 1.05, filter: "grayscale(1) brightness(0.75) contrast(1.15)" }}
        transition={{ duration: 2.2, delay: 0.4, ease: EASE }}
        className="h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-tr from-red-950/70 via-transparent to-transparent mix-blend-multiply" />
      <div className="absolute inset-x-0 top-0 scanline" />
      <div className="absolute inset-0 tac-grid opacity-60" />
    </div>
    <Corner pos="top-3 left-3 border-t-2 border-l-2" />
    <Corner pos="top-3 right-3 border-t-2 border-r-2" />
    <Corner pos="bottom-3 left-3 border-b-2 border-l-2" />
    <Corner pos="bottom-3 right-3 border-b-2 border-r-2" />
    <div className="absolute top-6 left-7 right-7 flex justify-between font-mono text-[11px] sm:text-xs tracking-widest text-white">
      <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-red-600 blink" />REC</span>
      <LiveClock />
    </div>
    <div className="absolute bottom-6 left-7 right-7 flex justify-between font-mono text-[11px] sm:text-xs tracking-widest text-zinc-200">
      <span>{label}</span><span>4K · H.265+</span>
    </div>
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-16 w-16 border border-red-500/70">
      <span className="absolute left-1/2 top-[-12px] h-3 w-px bg-red-500" />
      <span className="absolute left-1/2 bottom-[-12px] h-3 w-px bg-red-500" />
      <span className="absolute top-1/2 left-[-12px] w-3 h-px bg-red-500" />
      <span className="absolute top-1/2 right-[-12px] w-3 h-px bg-red-500" />
    </div>
  </motion.div>
);

export const Hero = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 110, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-11, 11]), { stiffness: 110, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const feedY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative min-h-screen overflow-hidden grain cursor-crosshair">
      <div className="absolute inset-0 tac-grid" />
      <div className="absolute -top-40 -left-40 h-[640px] w-[640px] rounded-full bg-red-700/20 blur-[140px]" />
      <div className="absolute bottom-0 right-0 h-[420px] w-[620px] bg-gradient-to-tl from-red-950/60 to-transparent blur-3xl" />

      <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 pt-32 lg:pt-36 pb-20 grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="font-mono text-xs uppercase tracking-[0.25em] text-red-500 flex items-center gap-3" data-testid="hero-eyebrow">
            <span className="h-2 w-2 bg-red-600 red-glow" />{t.hero.eyebrow}
          </motion.p>
          <h1 key={lang} className="mt-8 font-display font-black uppercase tracking-tight text-white text-[3.4rem] leading-[0.88] sm:text-8xl lg:text-[8.5rem]" data-testid="hero-title">
            {t.hero.lines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "metal-red" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.1, delay: 0.45 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.0 }} className="mt-8 max-w-xl text-base sm:text-lg text-zinc-300 leading-relaxed" data-testid="hero-subtitle">
            {t.hero.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.15 }} className="mt-10 flex flex-col sm:flex-row gap-4">
            <button data-testid="hero-book-button" onClick={() => scrollToId("booking")} className="group clip-btn bg-red-600 hover:bg-red-500 transition-colors px-7 py-4 font-mono text-xs sm:text-sm uppercase tracking-[0.18em] text-white flex items-center justify-center gap-3 red-glow">
              {t.hero.cta}<ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button data-testid="hero-services-button" onClick={() => scrollToId("services")} className="clip-btn border border-white/20 hover:border-white/60 hover:bg-white/5 transition-colors px-7 py-4 font-mono text-xs sm:text-sm uppercase tracking-[0.18em] text-white">
              {t.hero.cta2}
            </button>
          </motion.div>
          <motion.dl initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.35 }} className="mt-14 grid grid-cols-3 max-w-lg border-t border-white/10">
            {t.hero.stats.map(([v, l]) => (
              <div key={l} className="pt-5 pr-2 sm:pr-4" data-testid={`hero-stat-${v}`}>
                <dt className="font-display font-extrabold text-3xl sm:text-4xl text-white">{v}</dt>
                <dd className="font-sans sm:font-mono text-xs uppercase tracking-wide sm:tracking-widest text-zinc-400 mt-1">{l}</dd>
              </div>
            ))}
          </motion.dl>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.5, ease: EASE }} className="lg:col-span-5">
          <CameraFeed rx={rx} ry={ry} y={feedY} label={t.hero.feed} />
        </motion.div>
      </div>
      <button data-testid="hero-scroll-indicator" onClick={() => scrollToId("services")} className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400 hover:text-white">
        {t.hero.scroll}<ArrowDown className="h-4 w-4 animate-bounce text-red-500" />
      </button>
    </section>
  );
};
